import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isEmpty, isNil } from 'lodash';
import moment from 'moment';
import { ApiService } from 'api';
import { SERVICES_VALUATIONS_REFETCH_ON_WINDOW_FOCUS } from 'common/constants/services';
import { RISK_FREE_RATES_QUERY_KEY } from 'common/constants/services/allocations';
import { GetRiskFreeRatesParams } from './useGetRiskFreeRatesByMarketDate.types';
import { DISPLAY_DATE_FORMAT } from '../../../../pages/Financials/data';

const useGetRiskFreeRatesByMarketDate = (params: GetRiskFreeRatesParams) => {
  const { marketDate, OPMInputs, shouldQueryAutomatically = false } = params;

  const inputsNeedingFetch = useMemo(
    () => OPMInputs.filter(input => input.storedRiskFreeRate == null || Number.isNaN(Number(input.storedRiskFreeRate))),
    [OPMInputs]
  );
  const storedRates = useMemo(
    () =>
      Object.fromEntries(
        OPMInputs.filter(input => input.storedRiskFreeRate != null && !Number.isNaN(Number(input.storedRiskFreeRate))).map(
          input => [input.scenarioIdOrRef, Number(input.storedRiskFreeRate)]
        )
      ),
    [OPMInputs]
  );

  const queryFn = useCallback(() => {
    const promiseMap = inputsNeedingFetch.reduce((sofar, opmInput) => {
      const date = opmInput.backsolveDate ?? marketDate;
      const promise = ApiService.apiObservationsRead(
        opmInput.maturity.toString(),
        moment(date).format(DISPLAY_DATE_FORMAT)
      );
      return {
        ...sofar,
        [opmInput.scenarioIdOrRef]: promise,
      };
    }, {} as { [key: string]: Promise<number> });
    return Promise.all(Object.values(promiseMap)).then(results =>
      results.reduce((sofar, result, index) => {
        const key = Object.keys(promiseMap)[index];
        return {
          ...sofar,
          [key]: result,
        };
      }, {})
    );
  }, [inputsNeedingFetch, marketDate]);

  // Never auto-fetch just because a stored rate is null. Callers refetch explicitly after
  // maturity/date changes (or Refresh All writes rates onto approaches before updateOPMInputs).
  const { data, error, isError, isFetchedAfterMount, isFetching, isLoading, refetch } = useQuery({
    enabled: !isNil(marketDate) && !isEmpty(inputsNeedingFetch) && shouldQueryAutomatically,
    meta: { persist: false },
    queryFn: () => queryFn(),
    queryKey: [RISK_FREE_RATES_QUERY_KEY, inputsNeedingFetch, marketDate],
    refetchOnWindowFocus: SERVICES_VALUATIONS_REFETCH_ON_WINDOW_FOCUS,
    staleTime: 0,
  });

  const mergedData = useMemo(
    () => (shouldQueryAutomatically ? storedRates : { ...(isFetchedAfterMount ? data : {}), ...storedRates }),
    [data, isFetchedAfterMount, shouldQueryAutomatically, storedRates]
  );

  return {
    data: mergedData,
    error,
    isError,
    isFetching,
    isLoading: isLoading && !isEmpty(inputsNeedingFetch),
    refetch,
  };
};

export default useGetRiskFreeRatesByMarketDate;
